import { useCallback, useEffect, useRef, useState } from "react";

type Data = {
  id: number;
  selected: boolean;
  /** Filtered out of the list, a range selected with shift skips it */
  hidden?: boolean;
};

/**
 * The selection of a list's items: a click selects or unselects one, a shift click the range from the previously
 * clicked item (selected or unselected like the clicked one becomes).
 */
export const useMultiSelect = (data: Data[]) => {
  const [selectedData, setSelectedData] = useState<Data[]>(data);
  const [previousIndex, setPreviousIndex] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const previousIndexRef = useRef(previousIndex);
  const selectedDataRef = useRef(selectedData);
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
    previousIndexRef.current = previousIndex;
  }, [previousIndex]);
  useEffect(() => {
    selectedDataRef.current = selectedData;
  }, [selectedData]);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  useEffect(() => {
    setSelectedIds(
      selectedData.filter((value) => value.selected).map((value) => value.id)
    );
  }, [selectedData]);

  const getIndexesToChange = useCallback((toIndex: number) => {
    // Nothing clicked before, the range is the clicked item
    const fromIndex = previousIndexRef.current ?? toIndex;
    const min = Math.min(toIndex, fromIndex);
    const max = Math.max(toIndex, fromIndex);
    return Array.from({ length: max - min + 1 }, (_, i) => min + i).filter(
      (index) => index === toIndex || !selectedDataRef.current[index]?.hidden
    );
  }, []);

  const onMultiselectClick = useCallback(
    (index: number, wasShiftPressed: boolean) => {
      if (wasShiftPressed) {
        const indexesToChange = getIndexesToChange(index);
        setSelectedData((prev) =>
          prev.map((value, i) => {
            if (indexesToChange.includes(i))
              return {
                ...value,
                selected: !selectedDataRef.current[index]?.selected,
              };
            return value;
          })
        );

        setPreviousIndex(index);
      } else {
        setPreviousIndex(index);
        setSelectedData((prev) => {
          const cloned = [...prev];
          cloned[index] = {
            ...cloned[index],
            selected: !cloned[index]?.selected,
          };
          return cloned;
        });
      }
    },
    [getIndexesToChange]
  );

  const reset = useCallback(() => {
    setSelectedData((prev) =>
      prev.map((value) => ({ ...value, selected: false }))
    );
    setSelectedIds([]);
  }, []);

  const setNewData = useCallback((newData: Omit<Data, "selected">[]) => {
    setSelectedData(
      newData.map((value) => ({
        ...value,
        selected: selectedIdsRef.current.includes(value.id),
      }))
    );
  }, []);

  return { selectedIds, onMultiselectClick, reset, setNewData };
};
